import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { Calendar, Clock, MapPin, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

export default function MyShifts() {
  const { user } = useAuth();
  const [myShifts, setMyShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calloutDialogOpen, setCalloutDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [calloutReason, setCalloutReason] = useState('');

  useEffect(() => {
    fetchMyShifts();
  }, []);

  const fetchMyShifts = async () => {
    try {
      const response = await api.get('/shifts/my-shifts');
      setMyShifts(response.data);
    } catch (error) {
      toast.error('Failed to load your shifts');
    } finally {
      setLoading(false);
    }
  };

  const handleCallout = async () => {
    if (!calloutReason.trim()) {
      toast.error('Please provide a reason for calling out');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('reason', calloutReason);
      
      await api.post(`/shifts/${selectedShift.id}/callout`, formData);
      toast.success('Callout notification sent to admin');
      setCalloutDialogOpen(false);
      setCalloutReason('');
      setSelectedShift(null);
      fetchMyShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit callout');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (assignment) => {
    const status = assignment.status;
    
    if (status === 'confirmed') {
      return (
        <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
          <CheckCircle className="w-4 h-4 mr-1" />
          Confirmed
        </span>
      );
    } else if (status === 'called_out') {
      return (
        <span className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
          <XCircle className="w-4 h-4 mr-1" />
          Called Out
        </span>
      );
    }
    return null;
  };

  const isUpcoming = (shiftDate) => {
    return new Date(shiftDate) > new Date();
  };

  const isPast = (shiftDate) => {
    return new Date(shiftDate) < new Date();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-purple-900">My Shifts</h2>
        <p className="text-gray-600 mt-1">View your scheduled shifts and manage callouts</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : myShifts.length === 0 ? (
        <Card className="p-12 text-center border-purple-100">
          <Calendar className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-purple-900 mb-2">No shifts scheduled</h3>
          <p className="text-gray-600">Sign up for available shifts to see them here</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Upcoming Shifts */}
          <div>
            <h3 className="text-lg font-semibold text-purple-900 mb-4">Upcoming Shifts</h3>
            <div className="space-y-4">
              {myShifts
                .filter(shift => isUpcoming(shift.shift_date))
                .map((shift) => {
                  const assignment = shift.assignments[0];
                  return (
                    <Card key={shift.id} className="p-6 border-purple-100 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-xl font-semibold text-purple-900 mb-1">{shift.title}</h4>
                          {getStatusBadge(assignment)}
                        </div>
                      </div>

                      {shift.description && (
                        <p className="text-gray-700 mb-4">{shift.description}</p>
                      )}

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Calendar className="w-5 h-5 text-purple-600" />
                          <span className="font-medium">{formatDate(shift.shift_date)}</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-700">
                          <Clock className="w-5 h-5 text-purple-600" />
                          <span>{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</span>
                        </div>

                        {shift.location && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <MapPin className="w-5 h-5 text-purple-600" />
                            <span>{shift.location}</span>
                          </div>
                        )}
                      </div>

                      {assignment.status === 'called_out' && assignment.callout_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-red-800">
                            <strong>Callout Reason:</strong> {assignment.callout_reason}
                          </p>
                        </div>
                      )}

                      {assignment.status === 'confirmed' && (
                        <Button
                          onClick={() => {
                            setSelectedShift(shift);
                            setCalloutDialogOpen(true);
                          }}
                          variant="outline"
                          className="w-full text-red-600 border-red-600 hover:bg-red-50"
                          data-testid={`callout-shift-${shift.id}`}
                        >
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Call Out of This Shift
                        </Button>
                      )}
                    </Card>
                  );
                })}
              
              {myShifts.filter(shift => isUpcoming(shift.shift_date)).length === 0 && (
                <Card className="p-8 text-center border-purple-100">
                  <p className="text-gray-600">No upcoming shifts scheduled</p>
                </Card>
              )}
            </div>
          </div>

          {/* Past Shifts */}
          <div>
            <h3 className="text-lg font-semibold text-purple-900 mb-4">Past Shifts</h3>
            <div className="space-y-4">
              {myShifts
                .filter(shift => isPast(shift.shift_date))
                .slice(0, 5)
                .map((shift) => {
                  const assignment = shift.assignments[0];
                  return (
                    <Card key={shift.id} className="p-6 border-gray-200 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-700 mb-1">{shift.title}</h4>
                          {getStatusBadge(assignment)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatDate(shift.shift_date)}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              
              {myShifts.filter(shift => isPast(shift.shift_date)).length === 0 && (
                <Card className="p-8 text-center border-gray-200 bg-gray-50">
                  <p className="text-gray-600">No past shifts to display</p>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Callout Dialog */}
      <Dialog open={calloutDialogOpen} onOpenChange={setCalloutDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Call Out of Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedShift && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="font-semibold text-purple-900">{selectedShift.title}</p>
                <p className="text-sm text-gray-700">{formatDate(selectedShift.shift_date)}</p>
                <p className="text-sm text-gray-700">
                  {formatTime(selectedShift.start_time)} - {formatTime(selectedShift.end_time)}
                </p>
              </div>
            )}

            <div>
              <Label>Reason for Callout *</Label>
              <Textarea
                value={calloutReason}
                onChange={(e) => setCalloutReason(e.target.value)}
                placeholder="Please provide a reason for calling out..."
                rows={4}
                required
              />
              <p className="text-sm text-gray-600 mt-2">
                Your administrator will be notified immediately.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setCalloutDialogOpen(false);
                  setCalloutReason('');
                  setSelectedShift(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCallout}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Submit Callout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
